import React, { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import api from "../lib/api";
import { useTheme, colorPresets } from "../contexts/ThemeContext";
import { Button } from "./ui/button";
import { Badge } from "./ui/badge";
import { toast } from "sonner";
import { createTicketRefresher, handleEmbedMessage, postToEmbed, themedEmbedUrl } from "../lib/optimuszappEmbed";
import {
  MessageCircle,
  RefreshCw,
  ExternalLink,
  Maximize2,
  Minimize2,
  AlertCircle,
  Phone,
  ShieldCheck,
  Sparkles,
  ArrowRight,
  UserCheck,
} from "lucide-react";

export function MemberWhatsAppTab({ member }) {
  const navigate = useNavigate();
  const { theme, colorPreset } = useTheme();
  const iframeRef = useRef(null);
  const containerRef = useRef(null);

  const [loading, setLoading] = useState(true);
  const [chatData, setChatData] = useState(null);
  const [isFullscreen, setIsFullscreen] = useState(false);
  // The chat iframe whose refresh ticket was refused (bounded retries gave up) shows Retry instead of the iframe.
  const [stoppedChatKey, setStoppedChatKey] = useState(null);
  const ticketRefresher = useRef(null);
  if (!ticketRefresher.current) {
    ticketRefresher.current = createTicketRefresher({
      onGiveUp: (key, err) => {
        console.warn("[MEMBER_WHATSAPP] Chat refresh ticket refused; waiting for Retry:", err?.response?.status || err?.message);
        setStoppedChatKey(key);
      },
    });
  }

  // Compute resolved dark/light theme
  const getResolvedTheme = useCallback(() => {
    if (theme === "system") {
      return typeof window !== "undefined" &&
        window.matchMedia("(prefers-color-scheme: dark)").matches
        ? "dark"
        : "light";
    }
    return theme || "light";
  }, [theme]);

  const activePrimary = colorPresets[colorPreset]?.primary || "4 90% 58%";

  // Fetch fresh ephemeral SOC 2 launch URL for the current member
  const fetchChatUrl = useCallback(
    async (showToast = false) => {
      const memberId = member?.id || member?._id;
      if (!memberId) return;

      try {
        setLoading(true);
        const res = await api.get(`/v1/whatsapp/members/${memberId}/chat-url`);

        if (res.data?.success && res.data?.chat_url) {
          // Only an /embed/ screen on the embed_origin it came with; theme parameters prevent a white flash.
          const chatUrl = themedEmbedUrl(res.data.chat_url, res.data.embed_origin, {
            theme: getResolvedTheme(),
            preset: colorPreset || "electric",
            primary: activePrimary,
            hide_header: "true",
          });
          if (!chatUrl) {
            setChatData({ connected: true, error: "Unexpected WhatsApp chat address" });
            return;
          }
          setChatData({ ...res.data, chat_url: chatUrl });
          if (showToast) {
            toast.success("WhatsApp chat session refreshed");
          }
        } else if (res.data?.connected === false) {
          setChatData({
            connected: false,
            message:
              res.data.message ||
              "WhatsApp Workspace is not connected. Please connect WhatsApp in WhatsApp Hub first.",
          });
        } else if (res.data?.error) {
          setChatData({
            connected: true,
            error: res.data.error,
          });
        }
      } catch (err) {
        console.error("[MEMBER_WHATSAPP] Failed to load chat URL:", err);
        const detail = err?.response?.data?.detail;
        const errMsg =
          (typeof detail === "object" ? detail?.message : detail) ||
          err?.response?.data?.error ||
          err?.response?.data?.message ||
          err?.message ||
          "Failed to load WhatsApp conversation";
        if (showToast) {
          toast.error(errMsg);
        }
        setChatData({
          connected: false,
          error: errMsg,
        });
      } finally {
        setLoading(false);
      }
    },
    [member?.id, member?._id, getResolvedTheme, colorPreset, activePrimary]
  );

  useEffect(() => {
    fetchChatUrl();
  }, [fetchChatUrl]);

  // Fullscreen change handler
  useEffect(() => {
    const handleFullscreenChange = () => {
      setIsFullscreen(Boolean(document.fullscreenElement));
    };
    document.addEventListener("fullscreenchange", handleFullscreenChange);
    return () =>
      document.removeEventListener("fullscreenchange", handleFullscreenChange);
  }, []);

  const toggleFullscreen = () => {
    if (!containerRef.current) return;
    if (!document.fullscreenElement) {
      containerRef.current.requestFullscreen?.().catch((err) => {
        console.warn("Fullscreen request failed:", err);
      });
    } else {
      document.exitFullscreen?.().catch((err) => {
        console.warn("Exit fullscreen failed:", err);
      });
    }
  };

  const embedOrigin = chatData?.embed_origin || "";

  // Broadcast real-time theme & preset updates to the embedded chat (only to the embed_origin its launch URL came with)
  const sendThemeToIframe = useCallback(() => {
    const target = iframeRef.current?.contentWindow;
    if (!target) return;
    const resolved = getResolvedTheme();
    postToEmbed(target, embedOrigin, { type: "OPTIMUSZAPP_THEME_CHANGE", theme: resolved });
    postToEmbed(target, embedOrigin, {
      type: "OPTIMUSZAPP_THEME_UPDATE",
      payload: {
        theme: resolved,
        colorPreset,
        primary: activePrimary,
        accent: activePrimary,
        fontFamily: "Manrope",
        headingFontFamily: "Barlow Condensed",
        monoFontFamily: "JetBrains Mono",
      },
    });
  }, [getResolvedTheme, colorPreset, activePrimary, embedOrigin]);

  useEffect(() => {
    sendThemeToIframe();
  }, [sendThemeToIframe]);

  // Messages from the chat iframe only: ready, and a refresh ticket of its own for this member's chat.
  useEffect(() => {
    const memberRef = member?.id || member?._id;
    const handleWindowMessage = (event) =>
      handleEmbedMessage(event, {
        frames: [{ surface: "chat", memberId: memberRef, window: iframeRef.current?.contentWindow, origin: embedOrigin }],
        requestTicket: () =>
          ticketRefresher.current.request(`chat:${memberRef}`, async () => {
            const res = await api.post("/v1/whatsapp/launch-ticket", { surface: "chat", member_id: String(memberRef || "") });
            return res.data;
          }),
        onReady: () => sendThemeToIframe(),
      });
    window.addEventListener("message", handleWindowMessage);
    return () => window.removeEventListener("message", handleWindowMessage);
  }, [sendThemeToIframe, embedOrigin, member?.id, member?._id]);

  const memberId = member?.id || member?._id;
  const memberPhone = member?.phone;
  const chatKey = `chat:${memberId}`;
  const chatStopped = stoppedChatKey === chatKey;

  // A new chat session (fresh launch URL) and refresh tickets answered again.
  const retryChat = () => {
    ticketRefresher.current.retry(chatKey);
    setStoppedChatKey(null);
    fetchChatUrl(true);
  };

  // ── 1. Not Connected State ──
  if (!loading && chatData?.connected === false && !chatData?.error) {
    return (
      <div className="rounded-xl border border-border bg-card p-8 text-center space-y-4 shadow-sm my-2">
        <div className="mx-auto w-14 h-14 rounded-2xl bg-emerald-500/10 border border-emerald-500/20 flex items-center justify-center text-emerald-500">
          <MessageCircle className="w-7 h-7" />
        </div>
        <div className="max-w-md mx-auto space-y-2">
          <h3 className="text-base font-bold font-heading uppercase tracking-wide text-foreground">
            WhatsApp Hub Not Connected
          </h3>
          <p className="text-xs text-muted-foreground leading-relaxed">
            {chatData.message ||
              "Connect your organization's WhatsApp Business workspace to enable embedded messaging, automated follow-ups, and live member chats."}
          </p>
        </div>
        <div className="pt-2">
          <Button
            onClick={() => navigate("/whatsapp")}
            className="bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-heading uppercase tracking-wide gap-2"
          >
            <span>Open WhatsApp Hub</span>
            <ArrowRight className="w-3.5 h-3.5" />
          </Button>
        </div>
      </div>
    );
  }

  // ── 2. Invalid or Missing Phone State ──
  if (!loading && chatData?.error && !chatData?.chat_url) {
    return (
      <div className="rounded-xl border border-amber-500/30 bg-amber-500/5 p-8 text-center space-y-4 shadow-sm my-2">
        <div className="mx-auto w-14 h-14 rounded-2xl bg-amber-500/10 border border-amber-500/20 flex items-center justify-center text-amber-500">
          <Phone className="w-7 h-7" />
        </div>
        <div className="max-w-md mx-auto space-y-2">
          <h3 className="text-base font-bold font-heading uppercase tracking-wide text-foreground">
            Valid Phone Number Required
          </h3>
          <p className="text-xs text-muted-foreground leading-relaxed">
            {chatData.error}
          </p>
          {memberPhone && (
            <div className="pt-1">
              <Badge variant="outline" className="font-mono text-xs border-amber-500/40 text-amber-600 dark:text-amber-400">
                Current Phone: {memberPhone}
              </Badge>
            </div>
          )}
        </div>
        <div className="pt-2 flex items-center justify-center gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={() => fetchChatUrl(true)}
            className="text-xs font-heading uppercase tracking-wide gap-1.5"
          >
            <RefreshCw className="w-3.5 h-3.5" />
            <span>Retry</span>
          </Button>
        </div>
      </div>
    );
  }

  // ── 3. Embedded Chat Container ──
  return (
    <div
      ref={containerRef}
      className={`rounded-xl border border-border bg-card overflow-hidden shadow-sm flex flex-col transition-all duration-300 ${
        isFullscreen ? "fixed inset-0 z-50 rounded-none border-none" : "h-[740px] min-h-[580px]"
      }`}
    >
      {/* Tab Header Bar */}
      <div className="flex flex-wrap items-center justify-between gap-3 px-4 py-2.5 bg-secondary/40 border-b border-border shrink-0">
        {/* Left: Member & Status Badges */}
        <div className="flex items-center gap-2.5 min-w-0">
          <div className="w-7 h-7 rounded-lg bg-emerald-500/10 border border-emerald-500/20 flex items-center justify-center text-emerald-500 shrink-0">
            <MessageCircle className="w-4 h-4" />
          </div>
          <div className="min-w-0">
            <div className="flex items-center gap-2">
              <span className="text-xs font-bold font-heading uppercase tracking-wide text-foreground truncate">
                {member?.name || "Member"}
              </span>
              {chatData?.phone && (
                <Badge
                  variant="outline"
                  className="font-mono text-[10px] px-1.5 py-0 bg-background text-emerald-600 dark:text-emerald-400 border-emerald-500/30 shrink-0"
                >
                  {chatData.phone}
                </Badge>
              )}
            </div>
            <div className="flex items-center gap-1.5 text-[11px] text-muted-foreground">
              <span className="inline-block w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse" />
              <span>OptimusZapp Live Thread</span>
            </div>
          </div>
        </div>

        {/* Right: Actions */}
        <div className="flex items-center gap-1.5">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => fetchChatUrl(true)}
            disabled={loading}
            className="h-8 px-2 text-xs text-muted-foreground hover:text-foreground"
            title="Refresh WhatsApp Session"
          >
            <RefreshCw className={`w-3.5 h-3.5 ${loading ? "animate-spin" : ""}`} />
          </Button>

          {chatData?.chat_url && (
            <Button
              variant="ghost"
              size="sm"
              onClick={async () => {
                // The iframe already used its ticket: a new window gets its own.
                try {
                  const res = await api.post("/v1/whatsapp/launch-ticket", { surface: "chat", member_id: String(memberId || "") });
                  const openUrl = themedEmbedUrl(res.data?.launch_url, res.data?.embed_origin, {
                    theme: getResolvedTheme(),
                    preset: colorPreset || "electric",
                    primary: activePrimary,
                  });
                  if (openUrl) window.open(openUrl, "_blank", "noopener");
                } catch (err) {
                  toast.error("Could not open the chat in a new window");
                }
              }}
              className="h-8 px-2 text-xs text-muted-foreground hover:text-foreground"
              title="Open Chat in New Window"
            >
              <ExternalLink className="w-3.5 h-3.5" />
            </Button>
          )}

          <Button
            variant="ghost"
            size="sm"
            onClick={toggleFullscreen}
            className="h-8 px-2 text-xs text-muted-foreground hover:text-foreground"
            title={isFullscreen ? "Exit Fullscreen" : "Fullscreen View"}
          >
            {isFullscreen ? (
              <Minimize2 className="w-3.5 h-3.5" />
            ) : (
              <Maximize2 className="w-3.5 h-3.5" />
            )}
          </Button>
        </div>
      </div>

      {/* Main Viewport Content */}
      <div className="flex-1 w-full relative overflow-hidden bg-background">
        {loading && !chatData?.chat_url ? (
          <div className="flex h-full w-full items-center justify-center p-8">
            <div className="flex flex-col items-center gap-3 text-center">
              <div className="relative">
                <div className="w-10 h-10 rounded-full border-2 border-primary/20 border-t-primary animate-spin" />
                <MessageCircle className="w-4 h-4 text-primary absolute inset-0 m-auto" />
              </div>
              <p className="text-xs text-muted-foreground font-mono tracking-wide">
                Authenticating SOC 2 session & mounting chat...
              </p>
            </div>
          </div>
        ) : chatData?.chat_url && !chatStopped ? (
          <iframe
            key={memberId}
            ref={iframeRef}
            src={chatData.chat_url}
            title={`WhatsApp Chat - ${member?.name || "Member"}`}
            className="w-full h-full border-none bg-background"
            allow="clipboard-write; camera; microphone"
          />
        ) : (
          <div className="flex h-full w-full items-center justify-center p-8">
            <div className="text-center space-y-3 max-w-sm">
              <AlertCircle className="w-8 h-8 text-muted-foreground mx-auto" />
              <p className="text-xs text-muted-foreground">
                Unable to load member conversation session.
              </p>
              <Button
                variant="outline"
                size="sm"
                onClick={retryChat}
                className="text-xs font-heading uppercase"
              >
                Retry
              </Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default MemberWhatsAppTab;
